import io from "socket.io-client";
import { getBackendUrl } from "../config";
import api from "../services/api";
import notificationPollingService from "./notificationPollingService";

// ✅ Log condicional para não poluir console em produção
const isDev = process.env.NODE_ENV === "development";
const log = (...args) => isDev && console.log(...args);
const logWarn = (...args) => console.warn(...args); // Warnings sempre aparecem
const logError = (...args) => console.error(...args); // Errors sempre aparecem

class SocketWorker {
  constructor(companyId, userId) {
    const sessionToken = api.defaults.headers.Authorization

    if (!SocketWorker.instance) {
      this.companyId = companyId
      this.userId = userId
      this.token = sessionToken
      this.socket = null;
      this.connectionStatus = "disconnected";
      this.lastPongTime = null;
      this.heartbeatInterval = null;
      this.statusChangeCallbacks = [];
      this.eventListeners = {};
      this.isConfiguring = false;
      this.isTabVisible = !document.hidden;
      this.wasDisconnected = false;
      this.reconnectCallbacks = [];
      this.tabReactivatedDebounce = null;
      
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
      
      this.configureSocket();
      SocketWorker.instance = this;

    } else if (
      sessionToken &&
      sessionToken !== SocketWorker.instance.token
    ) {
      // Token mudou (re-login ou refresh) - reconectar com novo token
      log("🔄 [SocketWorker] Token mudou - reconectando com novo token...");
      SocketWorker.instance.token = sessionToken;
      SocketWorker.instance.companyId = companyId;
      SocketWorker.instance.userId = userId;
      SocketWorker.instance.forceReconnect();
    }

    return SocketWorker.instance;
  }

  /**
   * ✅ Detecta quando a aba fica visível/invisível
   * - Aba invisível: pausa heartbeat (browsers throttleam timers)
   * - Aba volta a ser visível: retoma heartbeat e força sync se necessário
   */
  handleVisibilityChange() {
    const wasVisible = this.isTabVisible;
    this.isTabVisible = !document.hidden;
    
    if (this.isTabVisible && !wasVisible) {
      // Aba voltou a ser visível
      log("👁️ [SocketWorker] Aba visível - verificando conexão...");
      
      if (this.socket && this.socket.connected) {
        // Socket ainda conectado - reiniciar heartbeat
        this.lastPongTime = Date.now();
        this.startHeartbeat();
        
        // ✅ Emitir evento para componentes sincronizarem (pode ter perdido eventos)
        this.notifyTabReactivated();
      } else {
        // Socket desconectado - reconectar
        log("🔄 [SocketWorker] Socket desconectado durante inatividade - reconectando...");
        this.wasDisconnected = true;
        this.forceReconnect();
      }
    } else if (!this.isTabVisible && wasVisible) {
      // Aba ficou invisível - pausar heartbeat para evitar falsos timeouts
      log("💤 [SocketWorker] Aba invisível - pausando heartbeat...");
      this.stopHeartbeat();
    }
  }

  /**
   * ✅ Notifica que a aba foi reativada (para componentes sincronizarem)
   * Usa debounce de 1s para evitar múltiplos refetch se usuário alternar abas rapidamente
   */
  notifyTabReactivated() {
    // ✅ Debounce: cancelar notificação anterior se houver
    if (this.tabReactivatedDebounce) {
      clearTimeout(this.tabReactivatedDebounce);
    }
    
    this.tabReactivatedDebounce = setTimeout(() => {
      this.tabReactivatedDebounce = null;
      this.reconnectCallbacks.forEach(callback => {
        try {
          callback("tab-reactivated");
        } catch (error) {
          logError("[SocketWorker] Erro no callback de reativação:", error);
        }
      });
    }, 1000); // 1 segundo de debounce
  }

  /**
   * ✅ Registra callback para eventos de reconexão/reativação
   * Útil para componentes que precisam sincronizar estado após reconexão
   */
  onReconnect(callback) {
    this.reconnectCallbacks.push(callback);
    return () => {
      this.reconnectCallbacks = this.reconnectCallbacks.filter(cb => cb !== callback);
    };
  }

  configureSocket() {
    // Evitar múltiplas configurações simultâneas
    if (this.isConfiguring) {
      return;
    }

    // Se já existe socket conectado, não recriar
    if (this.socket && this.socket.connected) {
      return;
    }

    this.isConfiguring = true;

    // Se já existe socket desconectado, limpar antes de criar novo
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.socket = io(`${getBackendUrl()}/${this?.companyId}`, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000, // ✅ Limitar delay máximo
      reconnectionAttempts: Infinity,
      // ✅ Usar transporte websocket direto (mais confiável)
      transports: ["websocket", "polling"],
      // ✅ PERFORMANCE: Usar 'auth' ao invés de 'query' (recomendado Socket.IO v4)
      // userId é extraído do token JWT no backend
      auth: { token: this.token }
    });

    // Adicionar listeners apenas uma vez
    this.socket.on("connect", () => {
      // ✅ FIX: Null check - socket pode ter sido destruído antes do callback
      if (!this.socket) return;
      
      log("✅ [SocketWorker] Conectado:", this.socket.id);
      this.setConnectionStatus("connected");
      
      // ✅ Só iniciar heartbeat se aba estiver visível
      if (this.isTabVisible) {
        this.startHeartbeat();
      }
      
      // ✅ Se estava desconectado, notificar para sincronização
      if (this.wasDisconnected) {
        log("🔄 [SocketWorker] Reconexão detectada - notificando componentes...");
        this.wasDisconnected = false;
        this.notifyReconnection();
      }
    });

    this.socket.on("disconnect", (reason) => {
      log("❌ [SocketWorker] Desconectado:", reason);
      this.setConnectionStatus("disconnected");
      this.stopHeartbeat();
      this.wasDisconnected = true;
      
      // ✅ Se não foi desconexão intencional, o Socket.IO já reconecta automaticamente
      // Não chamar reconnectAfterDelay() para evitar conflito
      if (reason === "io server disconnect" && this.socket) {
        // Servidor forçou desconexão - reconectar manualmente
        this.socket.connect();
      }
    });

    this.socket.on("connect_error", (error) => {
      logWarn("⚠️ [SocketWorker] Erro de conexão:", error.message);
      this.setConnectionStatus("connecting");
    });

    // ✅ Socket.IO v4: usar eventos corretos de reconexão
    this.socket.io.on("reconnect", (attempt) => {
      log("🔄 [SocketWorker] Reconectado após", attempt, "tentativa(s)");
      this.setConnectionStatus("connected");
      if (this.isTabVisible) {
        this.startHeartbeat();
      }
      this.notifyReconnection();
    });

    this.socket.io.on("reconnect_attempt", (attempt) => {
      log("🔄 [SocketWorker] Tentativa de reconexão:", attempt);
      this.setConnectionStatus("connecting");
    });

    this.socket.io.on("reconnect_failed", () => {
      logError("❌ [SocketWorker] Falha definitiva na reconexão");
      this.setConnectionStatus("disconnected");
    });

    // Heartbeat pong response
    this.socket.on("pong", (data) => {
      // ✅ FIX: Verificar se socket ainda existe
      if (!this.socket) return;
      this.lastPongTime = data?.timestamp || Date.now();
    });

    this.isConfiguring = false;
  }

  /**
   * ✅ Notifica componentes que houve reconexão
   * Componentes devem usar isso para refetch de dados
   */
  notifyReconnection() {
    this.reconnectCallbacks.forEach(callback => {
      try {
        callback("reconnected");
      } catch (error) {
        logError("[SocketWorker] Erro no callback de reconexão:", error);
      }
    });
  }

  setConnectionStatus(status) {
    if (this.connectionStatus !== status) {
      this.connectionStatus = status;
      this.notifyStatusChange();
      
      // ✅ Notificar Polling Service sobre status do socket
      // Quando socket desconecta, polling assume; quando conecta, polling para
      notificationPollingService.setSocketStatus(status === "connected");
    }
  }

  notifyStatusChange() {
    this.statusChangeCallbacks.forEach(callback => {
      try {
        callback(this.connectionStatus, this.getConnectionMetrics());
      } catch (error) {
        logError("Error in status change callback:", error);
      }
    });
  }

  onStatusChange(callback) {
    this.statusChangeCallbacks.push(callback);
    return () => {
      this.statusChangeCallbacks = this.statusChangeCallbacks.filter(cb => cb !== callback);
    };
  }

  startHeartbeat() {
    // ✅ Não iniciar heartbeat se aba estiver invisível
    if (!this.isTabVisible) {
      log("💤 [SocketWorker] Aba invisível - heartbeat não iniciado");
      return;
    }
    
    this.stopHeartbeat();
    this.lastPongTime = Date.now();
    
    this.heartbeatInterval = setInterval(() => {
      // ✅ Verificar visibilidade da aba antes de processar
      if (!this.isTabVisible) {
        log("💤 [SocketWorker] Aba invisível - ignorando heartbeat check");
        return;
      }
      
      if (this.socket && this.socket.connected) {
        this.socket.emit("ping");
        
        // ✅ Verificar se recebeu pong nos últimos 90 segundos (mais tolerante)
        const timeSinceLastPong = Date.now() - (this.lastPongTime || 0);
        if (timeSinceLastPong > 90000) {
          logWarn("⚠️ [SocketWorker] Socket heartbeat timeout (90s) - verificando conexão...");
          
          // ✅ Verificar se realmente está desconectado antes de forçar reconexão
          if (!this.socket.connected) {
            logWarn("⚠️ [SocketWorker] Confirmado desconectado - reconectando...");
            this.wasDisconnected = true;
            this.forceReconnect();
          } else {
            // Socket diz que está conectado mas não recebe pong - resetar timer
            log("🔍 [SocketWorker] Socket reporta conectado - resetando timer...");
            this.lastPongTime = Date.now();
          }
        }
      }
    }, 30000); // Ping a cada 30 segundos
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  get connected() {
    return this.socket?.connected || false;
  }

  getConnectionStatus() {
    return this.connectionStatus;
  }

  getConnectionMetrics() {
    return {
      status: this.connectionStatus,
      connected: this.socket?.connected || false,
      lastPongTime: this.lastPongTime,
      timeSinceLastPong: this.lastPongTime ? Date.now() - this.lastPongTime : null,
      socketId: this.socket?.id || null
    };
  }

  on(event, callback) {
    this.connect();
    this.socket.on(event, callback);

    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  emit(event, data) {
    this.connect();
    this.socket.emit(event, data);
  }

  off(event, callback) {
    this.connect();
    if (this.eventListeners[event]) {
      if (callback) {
        this.socket.off(event, callback);
        this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
      } else {
        this.eventListeners[event].forEach(cb => this.socket.off(event, cb));
        delete this.eventListeners[event];
      }
    }
  }

  disconnect() {
    this.stopHeartbeat();
    
    // ✅ Remover listener de visibilidade
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    
    // ✅ Limpar debounce pendente
    if (this.tabReactivatedDebounce) {
      clearTimeout(this.tabReactivatedDebounce);
      this.tabReactivatedDebounce = null;
    }
    
    // ✅ Limpar callbacks para evitar memory leak
    this.reconnectCallbacks = [];
    this.statusChangeCallbacks = [];
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null
      this.instance = null
      this.setConnectionStatus("disconnected");
    }
    
    // ✅ Limpar singleton para permitir nova instância
    SocketWorker.instance = null;
  }

  reconnectAfterDelay() {
    // ✅ Socket.IO já gerencia reconexão automática
    // Este método é mantido para compatibilidade mas raramente usado
    setTimeout(() => {
      if (!this.socket || !this.socket.connected) {
        log("🔄 [SocketWorker] Tentando reconexão após delay...");
        this.wasDisconnected = true;
        this.connect();
      }
    }, 1000);
  }

  connect() {
    if (!this.socket) {
      this.configureSocket();
    }
  }

  forceReconnect() {
    log("🔄 [SocketWorker] Forçando reconexão do socket...");
    this.setConnectionStatus("connecting");
    this.stopHeartbeat();
    this.wasDisconnected = true;
    
    // Desconectar socket atual
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    // Reconectar após pequeno delay
    setTimeout(() => {
      // Criar novo socket
      this.configureSocket();
      
      // Restaurar listeners salvos após socket estar pronto
      if (this.socket) {
        // Aguardar socket estar conectado ou pronto
        const restoreListeners = () => {
          // ✅ FIX: Verificar se socket ainda existe antes de restaurar
          if (!this.socket) return;
          
          // `this.eventListeners` é a fonte de verdade do que os componentes
          // registraram e já reflete off()/on() feitos durante a janela de
          // reconexão. O socket novo não tem nenhum desses handlers, então o
          // critério para restaurar é "ainda não está no socket" (comparar com
          // `this.eventListeners` era sempre verdadeiro e não restaurava nada).
          const restoredConnectCallbacks = [];
          Object.keys(this.eventListeners).forEach(event => {
            this.eventListeners[event].forEach(callback => {
              // ✅ FIX: Verificar novamente se socket existe (pode ter sido destruído)
              if (!this.socket) return;
              
              if (!this.socket.listeners(event).includes(callback)) {
                this.socket.on(event, callback);
                if (event === "connect") {
                  restoredConnectCallbacks.push(callback);
                }
              }
            });
          });

          // O "connect" desta conexão já disparou quando chegamos aqui (o emitter
          // do socket.io itera sobre uma cópia da lista de listeners), então os
          // handlers de "connect" recém-restaurados não rodam para esta conexão.
          // Invocar manualmente uma vez para que os componentes reentrem nas rooms
          // (joinChatBox/joinTickets/joinNotification).
          if (this.socket && this.socket.connected) {
            restoredConnectCallbacks.forEach(callback => {
              try {
                callback();
              } catch (error) {
                logError("[SocketWorker] Erro ao reexecutar handler de connect após reconexão:", error);
              }
            });
          }
        };

        if (this.socket.connected) {
          restoreListeners();
        } else {
          // ✅ FIX: Usar listener que pode ser removido em caso de timeout
          const onConnect = () => {
            restoreListeners();
          };
          this.socket.once("connect", onConnect);
          
          // ✅ FIX: Timeout para remover listener órfão se socket nunca conectar
          setTimeout(() => {
            if (this.socket && !this.socket.connected) {
              this.socket.off("connect", onConnect);
            }
          }, 30000); // 30s timeout
        }
        
        if (!this.socket.connected) {
          this.socket.connect();
        }
      }
    }, 500);
  }

  /**
   * ✅ Verifica se está conectado
   */
  isConnected() {
    return this.socket?.connected || false;
  }

  /**
   * ✅ Força verificação de conexão e sync se necessário
   * Útil para componentes chamarem manualmente
   */
  checkAndSync() {
    if (!this.socket?.connected) {
      log("🔄 [SocketWorker] checkAndSync: não conectado, reconectando...");
      this.wasDisconnected = true;
      this.forceReconnect();
    } else {
      // Notifica para componentes sincronizarem
      this.notifyReconnection();
    }
  }
}

const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
